/**
 * Login / invite flow for the arrival screen.
 * Verifies invite or session tokens, then lazily imports the Babylon engine
 * (main.js) when the user clicks Enter. The identity/device widgets on the
 * screen (avatar color, optional camera/mic check, presence line) are wired
 * by src/ui/arrivalScreen.js.
 */
import { DEFAULT_ROLE, isValidRole } from './config/roles.js';
import { DEFAULT_LEVEL, isStartableLevel, LAYOUT_LEVEL_PREFIX } from './config/levels.js';
import { analytics } from './services/analytics.js';
import { setIdentity } from './state/sessionStore';
import { initBootProgressBridge } from './utils/bootProgress.js';
import { initArrivalScreen } from './ui/arrivalScreen.js';
import { requestEntryApproval } from './ui/entryApproval.js';
import { showBootScreen } from './ui/bootScreen.js';
import { hasCompletedOnboarding } from './ui/onboarding/onboardingStore.js';
import { runArrivalSequence, markArrivalActive } from './ui/onboarding/arrivalSequence.js';

export function initLoginFlow() {
  const playerNameInput = document.getElementById('arrival-name-input');
  const roomNameInput = document.getElementById('arrival-room-input');
  const loginForm = document.getElementById('arrival-form');
  const startBtn = document.getElementById('arrival-enter-btn');
  const inviteRequired = document.getElementById('invite-required');
  const inviteVerifying = document.getElementById('invite-verifying');
  const inviteError = document.getElementById('invite-error');
  const inviteErrorMessage = document.getElementById('invite-error-message');

  const IS_DEV = import.meta.env.DEV;

  // Invite gate enabled - visitors must arrive with a valid invite/session
  // link. Set to false to temporarily allow open access (shared room).
  const REQUIRE_INVITE = true;

  // While the invite gate is off, pin everyone to a single shared Photon room so
  // all visitors land together (multiplayer + theater sync only bridge within a
  // room). When REQUIRE_INVITE is restored, the invite/session path sets the room
  // per-invite again and this constant is unused.
  const OPEN_ACCESS_ROOM = 'oggi';

  // While the invite gate is off there is no server-signed role, so roles are
  // decided by arrival order: the first person in the room becomes the host,
  // everyone who joins after is a participant (guest). The actual host/guest
  // decision happens at Photon join time in multiplayer.js (only there do we
  // know who is already in the room); this is just the baseline role until then.
  // When REQUIRE_INVITE is restored, the role comes from the signed session
  // token instead and this arrival-order logic is bypassed.
  const OPEN_ACCESS_ROLE = 'guest';

  // Meeting role for the current session, resolved from the invite/session
  // token (server-authoritative) and applied in startExperience().
  let sessionRole = DEFAULT_ROLE;

  // Starting level for the current session, resolved from the invite/session
  // token and applied in startExperience() (window.START_LEVEL, read by
  // createScene.js). Open-access sessions keep the default.
  let sessionLevel = DEFAULT_LEVEL;

  // Floor-plan preview (?previewLevel=<uuid>, opened from the /admin/levels
  // editor): a solo walkthrough of a saved layout. Set during init() after
  // the pre-flight existence check succeeds.
  let previewActive = false;

  // Identity/device widgets (avatar color, optional camera/mic check,
  // presence line) - wired once; the gate decides when they activate.
  const arrival = initArrivalScreen();

  function showLoginForm() {
    loginForm.style.display = '';
    startBtn.style.display = '';
    inviteRequired.style.display = 'none';
    inviteVerifying.style.display = 'none';
    inviteError.style.display = 'none';
    arrival.onGatePassed({ roomName: roomNameInput.value.trim(), preview: previewActive });
  }

  function hideLoginForm() {
    loginForm.style.display = 'none';
    startBtn.style.display = 'none';
  }

  function showInviteRequired() {
    hideLoginForm();
    inviteRequired.style.display = '';
  }

  function showInviteError(message) {
    hideLoginForm();
    arrival.hideLobby();
    inviteVerifying.style.display = 'none';
    inviteErrorMessage.textContent = message;
    inviteError.style.display = '';
  }

  function showVerifying() {
    hideLoginForm();
    arrival.hideLobby();
    inviteVerifying.style.display = '';
  }

  // Verify an invite token against the server
  async function verifyInviteToken(token) {
    const res = await fetch('/api/auth/verify-invite', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    return { ok: res.ok, data: await res.json() };
  }

  /**
   * The invite gate with the pre-event lobby loop. A 403 EVENT_NOT_OPEN is a
   * valid invite held at a not-yet-open event: show the countdown lobby and
   * re-verify when it hits zero (the invite param is still in the URL - it is
   * only stripped on success - so a refresh mid-lobby re-enters here and the
   * server re-gates). Any other failure is terminal.
   * @param {string} inviteToken
   * @param {number} [reverifyAttempt] retries after doors-open (clock skew /
   *   transient errors); spaced 5s so the 5/min auth limiter is respected.
   */
  async function verifyInviteAndEnter(inviteToken, reverifyAttempt = 0) {
    let ok, data;
    try {
      ({ ok, data } = await verifyInviteToken(inviteToken));
    } catch {
      if (reverifyAttempt > 0 && reverifyAttempt < 3) {
        setTimeout(() => verifyInviteAndEnter(inviteToken, reverifyAttempt + 1), 5000);
        return;
      }
      showInviteError('Could not verify invite. Please check your connection and try again.');
      return;
    }

    if (ok && data.valid) {
      localStorage.setItem('oggi_session', data.sessionToken);
      localStorage.setItem('roomName', data.roomName);
      if (isValidRole(data.role)) sessionRole = data.role;
      if (isStartableLevel(data.level)) sessionLevel = data.level;
      roomNameInput.value = data.roomName;
      // Strip invite param from URL for clean UX
      const cleanUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
      arrival.hideLobby();
      showLoginForm();
      return;
    }

    if (data.code === 'EVENT_NOT_OPEN' && data.event) {
      // Hold in the lobby; the payload's doorsOpenAt/serverNow drive the
      // countdown. A rescheduled event just yields a fresh payload here.
      hideLoginForm();
      inviteRequired.style.display = 'none';
      inviteVerifying.style.display = 'none';
      inviteError.style.display = 'none';
      arrival.showLobby({
        title: data.event.title,
        startsAt: data.event.startsAt,
        doorsOpenAt: data.event.doorsOpenAt,
        timezone: data.event.timezone,
        serverNow: data.serverNow,
        onDoorsOpen: () => verifyInviteAndEnter(inviteToken, 1),
      });
      return;
    }

    if (data.code === 'EVENT_CANCELLED') {
      showInviteError('This event has been cancelled. Please contact the organizer for details.');
      return;
    }
    if (data.code === 'EVENT_ENDED') {
      showInviteError('This event has ended. Please contact the organizer for a new invite.');
      return;
    }
    const msg = data.error || 'This invite link is invalid.';
    showInviteError(msg + ' Please contact your sales representative for a new link.');
  }

  // Verify an existing session token
  async function verifySessionToken(sessionToken) {
    const res = await fetch('/api/auth/verify-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionToken }),
    });
    return { ok: res.ok, data: await res.json() };
  }

  // Main initialization
  async function init() {
    playerNameInput.value =
      localStorage.getItem('playerName') || `Player${Math.floor(Math.random() * 1000)}`;

    // Floor-plan preview from the /admin/levels editor. Checked before the
    // dev/open-access shortcut so preview works in dev too. The pre-flight
    // fetch is the gate: a bogus or deleted layout id shows an error and
    // never boots the engine, so ?previewLevel can't serve as an ungated
    // door into the exhibition. Preview sessions skip multiplayer entirely
    // (levelManager checks ?previewLevel), so no real room is touched.
    const previewParams = new URLSearchParams(window.location.search);
    const previewId = previewParams.get('previewLevel');
    if (previewId) {
      showVerifying();
      // Optional portal token from the world builder: widens the read to the
      // working draft so editors preview unpublished edits. Anonymous preview
      // of published (and legacy admin) layouts keeps working without it.
      const layoutPreviewToken = previewParams.get('layoutPreview') || '';
      const previewQuery = layoutPreviewToken
        ? `?preview=${encodeURIComponent(layoutPreviewToken)}`
        : '';
      const res = await fetch(`/api/levels/${encodeURIComponent(previewId)}${previewQuery}`).catch(
        () => null,
      );
      if (!res || !res.ok) {
        showInviteError('Preview layout not found. It may have been deleted.');
        return;
      }
      previewActive = true;
      window.LAYOUT_PREVIEW_TOKEN = layoutPreviewToken || undefined;
      sessionLevel = LAYOUT_LEVEL_PREFIX + previewId;
      roomNameInput.value = 'preview';
      showLoginForm();
      return;
    }

    // Dev mode (or gate disabled): skip invite check entirely and force the
    // shared room (overrides any stale per-browser roomName in localStorage).
    if (IS_DEV || !REQUIRE_INVITE) {
      roomNameInput.value = OPEN_ACCESS_ROOM;
      showLoginForm();
      return;
    }

    // Check for invite token in URL
    const params = new URLSearchParams(window.location.search);
    const inviteToken = params.get('invite');

    if (inviteToken) {
      showVerifying();
      await verifyInviteAndEnter(inviteToken);
      return;
    }

    // No invite in URL -- check for existing session
    const existingSession = localStorage.getItem('oggi_session');
    if (existingSession) {
      try {
        const { ok, data } = await verifySessionToken(existingSession);
        if (ok && data.valid) {
          if (isValidRole(data.role)) sessionRole = data.role;
          if (isStartableLevel(data.level)) sessionLevel = data.level;
          roomNameInput.value = data.roomName;
          showLoginForm();
          return;
        }
      } catch {
        /* fall through */
      }
      localStorage.removeItem('oggi_session');
    }

    // No valid session -- show invite required
    showInviteRequired();
  }

  function startExperience() {
    // Double-click guard: collect() below tears down device state and must
    // run exactly once.
    if (startBtn.disabled) return;
    startBtn.disabled = true;

    const playerName =
      playerNameInput.value.trim().substring(0, 16) || `Player${Math.floor(Math.random() * 1000)}`;
    // Open access: everyone shares one room, regardless of any stale localStorage
    // value (the cause of users splitting into separate instances).
    const roomName = !REQUIRE_INVITE
      ? OPEN_ACCESS_ROOM
      : roomNameInput.value.trim().substring(0, 24) || 'default';
    // Meeting role (§2.5): open-access demos assign roles by arrival order (first
    // in the room hosts) starting from the OPEN_ACCESS_ROLE baseline; invite/
    // session flows use the server-signed role resolved during init().
    const userRole = !REQUIRE_INVITE ? OPEN_ACCESS_ROLE : sessionRole;
    // Starting level: previews force the layout under preview; open access
    // always uses the default; invite/session flows use the level resolved
    // during init(). createScene.js re-validates it either way.
    const startLevel = previewActive
      ? sessionLevel
      : !REQUIRE_INVITE
        ? DEFAULT_LEVEL
        : sessionLevel;
    localStorage.setItem('playerName', playerName);
    // Previews keep the placeholder room out of localStorage so they never
    // clobber the operator's real room.
    if (!previewActive) localStorage.setItem('roomName', roomName);
    localStorage.setItem('userRole', userRole);
    setIdentity({ playerName, roomName, userRole });
    window.START_LEVEL = startLevel;
    // Open access has no server-signed role: let multiplayer.js promote the
    // first person in the room to host. Invite/session roles are authoritative
    // and must not be overridden by arrival order.
    window.AUTO_HOST_FIRST = !REQUIRE_INVITE;

    analytics.init(); // session_start — globals above must be set first

    // Gather identity/device choices and release any preview hardware, then
    // leave the arrival screen and boot. Entering never blocks on devices:
    // collect() falls back to default join prefs when the check was skipped.
    const { colorExplicitlyChosen, deviceCheckUsed, prefs } = arrival.collect();
    analytics.track('arrival_enter', {
      camOff: !!prefs.audioOnly,
      micMuted: !!prefs.micMuted,
      colorChosen: colorExplicitlyChosen,
      deviceCheckUsed,
    });

    // Waiting-room approval gate: invite guests may have to wait for a host.
    // Everyone else (open access, dev, bypass roles, lobby off) resolves
    // 'admitted' without the UI ever flashing. collect() above already closed
    // the preview tracks, so the camera light is out while waiting.
    // The form stays up (with Enter disabled) across the request-entry round
    // trip - swapping it out earlier would show an empty panel for the ~100ms
    // that every guest, gated or not, spends here.
    const gate = requestEntryApproval({
      onWaiting: () => {
        hideLoginForm();
        arrival.showApproval({ phase: 'waiting', onCancel: () => gate.cancel() });
      },
    });
    gate.promise.then((status) => {
      if (status === 'admitted') {
        arrival.hideApproval();
        document.getElementById('arrival-screen').style.display = 'none';
        enterExperience();
      } else if (status === 'denied') {
        arrival.showApproval({ phase: 'denied', onCancel: () => window.location.reload() });
      } else {
        // Canceled: the arrival screen is a one-shot (devices are torn down),
        // so a fresh load is the honest way back.
        window.location.reload();
      }
    });
  }

  // Boot the Babylon engine behind the boot screen (progress bar + tips +,
  // for first-run users, the inline comfort question). startApp() resolves
  // only once the level is fully loaded and the render loop is running (or
  // SCENE_ERROR is set); the reveal additionally waits for the question so
  // the world appears with no modal in the way.
  async function enterExperience() {
    // Solo floor-plan previews skip the comfort question - the operator is
    // inspecting a layout, not learning the controls.
    const needsOnboarding = !previewActive && !hasCompletedOnboarding();
    markArrivalActive(true);
    const boot = showBootScreen({ needsOnboarding });
    let app;
    try {
      const m = await enginePromise;
      app = await m.startApp();
    } catch (err) {
      console.error('[LoginFlow] Boot failed:', err);
      boot.showError(err?.message);
      markArrivalActive(false);
      return;
    }
    if (window.SCENE_ERROR) {
      boot.showError(String(window.SCENE_ERROR));
      markArrivalActive(false);
      return;
    }
    const choice = await boot.choicePromise; // null for returning users
    document.getElementById('renderCanvas').style.display = '';
    // The canvas just gained real dimensions (it was display:none through the
    // whole load). Nudge the engine and every rect-caching overlay (e.g. the
    // theater's iframe projector) to re-measure.
    window.dispatchEvent(new Event('resize'));
    await boot.hide();
    await runArrivalSequence(app, { choice, firstRun: needsOnboarding });
  }

  // Warm up the ~5MB engine chunk while the user reads the menu / types their
  // name, so the Start click is near-instant. main.js is side-effect-free on
  // import (the engine starts only when startApp() is called).
  const enginePromise = import('./main.js');
  // Route the level's asset-load events into the boot progress model before
  // any boot can start.
  initBootProgressBridge();

  startBtn.onclick = startExperience;
  init();
}
